/**
 * A utility function designed to safely handle errors of unknown type and return
 * a descriptive error message. It ensures type safety and provides a fallback
 * message for non-standard error objects.
 * @param {unknown} error - The error object to be processed. This can be of any type, as JavaScript errors are not guaranteed to follow a specific structure.
 * @returns {string} - A human-readable error message. If the error is an instance of `Error`, it returns the `message` property. Otherwise, it returns 'Unknown error'.
 */
export const handleError = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  return 'Unknown error';
};

/**
 * A utility function to transform a string returning it with the first letter
 * capitalized.
 * @param str - The original string.
 * @returns The formatted string with the first letter capitalized.
 */
export const capitalizeFirstLetter = (str: string): string => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * A utility function to transform a string returning it with the first letter
 * in lower case.
 * @param str - The original string.
 * @returns The formatted string with the first letter in lower case.
 */
export const lowercaseFirstLetter = (str: string): string => {
  if (!str) return "";
  return str.charAt(0).toLowerCase() + str.slice(1);
}

/**
 * Formata um timestamp que veio do backend em UTC mas SEM o marcador de fuso
 * (ex.: "2026-09-02T15:52:42.689826"). Sem o "Z", o `new Date()` do navegador
 * assume horario LOCAL e a hora sai 3h adiantada no Brasil (GMT-3). Aqui a
 * leitura e forcada como UTC antes de converter pro fuso do usuario.
 * @param iso - timestamp ISO do backend (com ou sem marcador de fuso).
 * @returns data/hora local formatada, ou "-" quando vazio/invalido.
 */
export const dataHoraDeUtc = (iso: string | null | undefined): string => {
  if (!iso) return "-";
  const temFuso = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(iso);
  const d = new Date(temFuso ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleString();
};